/**
 * RV Smart Alerts — trigger utility
 * Calls the detect-matches edge function to run the existing matching pipeline
 * and store quality match alerts. Fire-and-forget: errors are logged but never
 * block the user's primary action (creating/updating a buyer or property).
 */

export type TriggerSource = 'buyer_new' | 'buyer_updated' | 'property_new' | 'property_updated';
export type TargetType = 'buyer' | 'property';

import { auth } from '../firebase';

/**
 * Fire a match detection trigger. Non-blocking — returns a promise that
 * callers can await or ignore. Errors are caught and logged, never thrown.
 */
export async function triggerMatchDetection(
  triggerSource: TriggerSource,
  targetId: string,
  targetType: TargetType
): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user) return;

    const token = await user.getIdToken();
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/detect-matches`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ triggerSource, targetId, targetType }),
    });

    if (!res.ok) {
      const errBody = await res.text();
      console.warn('[Smart Alerts] detect-matches returned non-OK:', res.status, errBody);
      return;
    }

    const data = await res.json();
    if (data.success) {
      console.log(`[Smart Alerts] ${triggerSource}: ${data.matchesFound} matches, ${data.alertsCreated} new, ${data.alertsUpdated} updated, ${data.alertsSkipped} skipped (${data.executionMs}ms)`);
    } else if (data.error) {
      console.warn('[Smart Alerts] detect-matches error:', data.error);
    }
  } catch (err) {
    console.warn('[Smart Alerts] trigger failed (non-blocking):', err);
  }
}

// ── Relevant field detection for update triggers ──────────────

const BUYER_RELEVANT_FIELDS = [
  'cities', 'neighborhoods', 'priceMin', 'priceMax',
  'rooms', 'minSize', 'maxSize', 'propertyTypes',
  'parkingRequired', 'elevatorRequired', 'saferoomRequired', 'balconyRequired',
  'conditionPreferences',
];

const PROPERTY_RELEVANT_FIELDS = [
  'price', 'rooms', 'size', 'city', 'neighborhood',
  'propertyType', 'hasParking', 'hasElevator', 'hasSaferoom', 'hasBalcony',
  'propertyCondition',
];

/**
 * Deep-compare two values for relevance. Handles primitives, arrays (order-insensitive), and objects.
 */
function valuesDiffer(a: any, b: any): boolean {
  if (a === b) return false;
  if (a == null || b == null) return a != null || b != null;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return true;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.some((v, i) => v !== sortedB[i]);
  }
  if (typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) !== JSON.stringify(b);
  }
  return a !== b;
}

/**
 * Check if a buyer update touched any of the matching-relevant fields.
 * searchCriteria fields are nested under the searchCriteria object.
 */
export function buyerRelevantFieldsChanged(
  before: Record<string, any>,
  after: Record<string, any>
): boolean {
  const beforeSc = before.searchCriteria || {};
  const afterSc = after.searchCriteria || {};
  for (const field of BUYER_RELEVANT_FIELDS) {
    if (valuesDiffer(beforeSc[field], afterSc[field])) return true;
  }
  return false;
}

/**
 * Check if a property (form) update touched any of the matching-relevant fields.
 * Property fields are nested under the property object; address fields under address.
 */
export function propertyRelevantFieldsChanged(
  before: Record<string, any>,
  after: Record<string, any>
): boolean {
  const beforeProp = before.property || {};
  const afterProp = after.property || {};
  const beforeAddr = before.address || {};
  const afterAddr = after.address || {};

  for (const field of PROPERTY_RELEVANT_FIELDS) {
    // Check in property sub-object first, then address sub-object
    if (field in beforeProp || field in afterProp) {
      if (valuesDiffer(beforeProp[field], afterProp[field])) return true;
    }
    if (field === 'city' || field === 'neighborhood') {
      if (valuesDiffer(beforeAddr[field], afterAddr[field])) return true;
    }
  }
  return false;
}
